import React, { useState, useEffect } from 'react';
import { PhoneNumberUtil } from "google-libphonenumber";
import { Container, Form, FormGroup, Input, Label, Button, Alert, Spinner } from 'reactstrap';
import SectionDivider from '../../../components/SectionDivider/SectionDivider';
import { useNavigate } from "react-router-dom";
import { InitialAccountDataStorageKey } from '../../../constants/localStorage';
import { useAuth } from "react-oidc-context";
import { validateExistingEmailAddress } from '../../../utilities/orgPanelApiCalls';
import { EMAIL_REGEX } from '../../../constants/regex';
import { DcgAutomation } from '../../../constants/onboardingVersions';

const PortalAdminInfoPageContent = () => {
    const phoneUtil = PhoneNumberUtil.getInstance();
    const navigate = useNavigate();
    const auth = useAuth();
    const [isLoading, setIsLoading] = useState(false);
    const [manageData, setData] = useState(() => {
        const manageData = JSON.parse(localStorage.getItem(InitialAccountDataStorageKey));

        return manageData || {};
    });

    const checkIfEmailAlreadyExistsInDcgPortal = async (emailAddress) => {
        var result = await validateExistingEmailAddress(emailAddress);
        return result.data.emailAlreadyExistsInDcgPortal;
    };

    useEffect(() => {
        if (auth.isAuthenticated) {
            setData({
                ...manageData,
                firstName: auth.user.profile.given_name,
                lastName: auth.user.profile.family_name,
                email: auth.user.profile.email,
                confirmEmail: auth.user.profile.email
            });
        }
    }, [auth]);

    useEffect(() => {
        if (manageData) {
            localStorage.setItem(InitialAccountDataStorageKey, JSON.stringify(manageData));
        }

    }, [manageData]);
    

    // Manage error state for validation
    const [errors, setErrors] = useState({
        phone: '',
        email: '',
        confirmEmail: ''
    });

    // Handle input changes
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setData((prevState) => ({
            ...prevState,
            [name]: value
        }));
    };

    // Validate form before submission
    const validateForm = async () => {
        setIsLoading(true);
        const newErrors = {};

        // Phone number validation (e.g., must be 10 digits)
        let isValidPhone = false;
        try {
            const number = phoneUtil.parse("+1-" + manageData.phone);
            isValidPhone = phoneUtil.isValidNumber(number);
        } catch { }
        if (!isValidPhone) {
            newErrors.phone = '** Please enter a valid phone number in XXX-XXX-XXXX format';
        }

        // Email validation (basic regex for format)
        if (!EMAIL_REGEX.test(manageData.email)) {
            newErrors.email = 'Invalid email format.';
        }

        // Confirm email validation
        if (manageData.email !== manageData.confirmEmail) {
            newErrors.confirmEmail = 'Email and Confirm Email must match.';
        }
        if (!newErrors.email) {
            var result = await checkIfEmailAlreadyExistsInDcgPortal(manageData.email);
            if (result) {
                newErrors.email = 'The email address supplied is linked to another DCG portal. Please login and access the portal currently linked to this email address.';
            }
            if (typeof result === 'undefined') {
                newErrors.email = 'Server could not process the request';
            }
        }
        if (auth.isAuthenticated && (manageData.email !== auth.user.profile.email)) {
            newErrors.email = 'User is already logged in with a different email address';
        }
        

        // Set the new errors
        setErrors(newErrors);
        setIsLoading(false);
        // Return whether the form is valid
        return Object.keys(newErrors).length === 0;
    };

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();

        // Perform validation
        const isValid = await validateForm();

        if (isValid) {
            // Proceed with form submission
            navigate(`/${DcgAutomation}/createHQLocation`);
        }
    };
    const submitBtnTxt = isLoading ? <Spinner size="sm" /> : "NEXT"
    return (
        <Container className="text-center">
            <Form onSubmit={handleSubmit}>
                <FormGroup className="mb-3">
                    <p className="text-center pt-0">Name your portal</p>
                    <Label for="portalName" hidden>
                        Portal Name
                    </Label>
                    <Input
                        id="portalName"
                        name="portalName"
                        placeholder="Portal Name"
                        type="text"
                        value={manageData.portalName}
                        onChange={handleInputChange}
                        required
                    />
                </FormGroup>

                <SectionDivider width="70%" />
                <p className="text-center pt-0">Enter the Portal Admin's information</p>

                <FormGroup className="mb-3">
                    <Label for="firstName" hidden>
                        First Name
                    </Label>
                    <Input
                        id="firstName"
                        name="firstName"
                        placeholder="First Name"
                        type="text"
                        value={manageData.firstName}
                        onChange={handleInputChange}
                        required
                    />
                </FormGroup>

                <FormGroup className="mb-3">
                    <Label for="lastName" hidden>
                        Last Name
                    </Label>
                    <Input
                        id="lastName"
                        name="lastName"
                        placeholder="Last Name"
                        type="text"
                        value={manageData.lastName}
                        onChange={handleInputChange}
                        required
                    />
                </FormGroup>

                <FormGroup className="mb-3">
                    <Label for="phoneNumber" hidden>
                        Phone Number
                    </Label>
                    <Input
                        id="phoneNumber"
                        name="phone"
                        placeholder="Phone Number"
                        type="text"
                        value={manageData.phone}
                        onChange={handleInputChange}
                    />
                    {errors.phone && <Alert color="danger">{errors.phone}</Alert>}
                </FormGroup>

                <FormGroup className="mb-3">
                    <Label for="email" hidden>
                        Email
                    </Label>
                    <Input
                        id="email"
                        name="email"
                        placeholder="Email"
                        type="email"
                        value={manageData.email}
                        onChange={handleInputChange}
                    />
                    {errors.email && <Alert color="danger">{errors.email}</Alert>}
                </FormGroup>

                <FormGroup className="mb-3">
                    <Label for="confirmEmail" hidden>
                        Confirm Email
                    </Label>
                    <Input
                        id="confirmEmail"
                        name="confirmEmail"
                        placeholder="Confirm Email"
                        type="email"
                        value={manageData.confirmEmail}
                        onChange={handleInputChange}
                    />
                    {errors.confirmEmail && <Alert color="danger">{errors.confirmEmail}</Alert>}
                </FormGroup>

                <Button className="btn-same-size" color="primary" disabled={isLoading} type="submit">
                    {submitBtnTxt}
                </Button>
            </Form>
        </Container>
    );
};

export default PortalAdminInfoPageContent;
